/**
 * `<RawRevealOverlay>` — wrapper that renders the diagonal watermark
 * banner whenever `rawActive === true` (cloud-VM supportability
 * TDD §B-1 "Watermark").
 *
 * Per the TDD's Codex round-1 MINOR fix: the watermark is a REAL
 * absolutely-positioned `<div>` (containing 12 `<span>` text tiles),
 * NOT a CSS `::before` pseudo-element. Reason: screenshot tools
 * universally capture real DOM nodes whereas pseudo-element capture
 * behaviour varies. Also lets screen readers announce the watermark
 * via `aria-label`.
 *
 * The overlay also renders an "Arm raw reveal" button (when not
 * armed) or a "Disarm" button (when armed) so engineers can flip the
 * `toggleArmed` state without keyboard-only access (TDD §B-1
 * requires `armToggle()` / `disarmToggle()` to be exposed; this is
 * the UI surface for them).
 *
 * Children render inside the overlay's positioning container. The
 * watermark sits above children when `rawActive` AND below the arm
 * button so the arm button stays clickable.
 */

import type { UseRawRevealState } from './hooks/useRawReveal';

export interface RawRevealOverlayProps {
  reveal: UseRawRevealState;
  children: React.ReactNode;
}

/** Number of watermark text tiles. Lots of small tiles produce a
 *  diagonal repeating pattern across the pane regardless of pane
 *  size. */
const WATERMARK_TILE_COUNT = 12;

export function RawRevealOverlay({
  reveal,
  children,
}: RawRevealOverlayProps): React.JSX.Element {
  return (
    <div
      data-testid="vm-debug-raw-reveal-overlay"
      data-raw-active={reveal.rawActive}
      data-toggle-armed={reveal.toggleArmed}
      className="relative flex flex-1 flex-col overflow-hidden"
    >
      {/* Arm / disarm button — top-right corner, always above the
          watermark so engineers can disarm without releasing Alt. */}
      <button
        type="button"
        data-testid="vm-debug-raw-reveal-toggle"
        onClick={
          reveal.toggleArmed ? reveal.disarmToggle : reveal.armToggle
        }
        aria-pressed={reveal.toggleArmed}
        className="absolute right-2 top-1.5 z-20 rounded-md border border-white/15 bg-black/40 px-2 py-0.5 text-[10px] text-white/70 hover:bg-black/60"
      >
        {reveal.toggleArmed ? 'Disarm raw' : 'Show raw'}
      </button>

      {children}

      {/* Watermark — real DOM nodes, NOT a CSS pseudo-element. Mounted
          ONLY when raw is active so the chrome stays clean otherwise.
          aria-label so screen readers announce the redact disclaimer
          when the engineer activates reveal. */}
      {reveal.rawActive ? (
        <div
          data-testid="vm-debug-raw-reveal-watermark"
          role="img"
          aria-label="Internal only - redact before sharing"
          className="pointer-events-none absolute inset-0 z-10 select-none overflow-hidden"
          style={{ opacity: 0.18 }}
        >
          <div
            className="absolute inset-0 grid"
            style={{
              gridTemplateColumns: 'repeat(3, 1fr)',
              gridTemplateRows: 'repeat(4, 1fr)',
              transform: 'rotate(-30deg) scale(1.4)',
              transformOrigin: 'center',
            }}
          >
            {Array.from({ length: WATERMARK_TILE_COUNT }).map((_, i) => (
              <span
                key={i}
                data-testid="vm-debug-raw-reveal-watermark-tile"
                className="flex items-center justify-center text-[11px] font-bold uppercase tracking-widest text-amber-300"
              >
                INTERNAL — REDACT BEFORE SHARING
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
